import { SnackbarCloseReason } from "@mui/material";
import { createContext, PropsWithChildren, useContext, useState } from "react";

export interface LayoutContextProps {
    loaderOpen: LoaderProps;
    alert: AlertProps;
    handleOpenLoader: (text?: string) => void;
    handleCloseLoader: () => void;
    handleSuccessAlert: (message: string) => void;
    handleErrorAlert: (message: string) => void;
    handleInfoAlert: (message: string) => void;
    handleWarningAlert: (message: string) => void;
    handleCloseAlert: (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => void;
}

export const LayoutContext = createContext<LayoutContextProps | null>(null);

export const LayoutProvider = ({ children }: PropsWithChildren) => {
    const [loaderOpen, setLoaderOpen] = useState<LoaderProps>({ open: false, text: "" });
    const [alert, setAlert] = useState<AlertProps>({ message: "", open: false, severity: "success" });

    const handleOpenLoader = (text = "") => setLoaderOpen({ open: true, text });
    const handleCloseLoader = () => setLoaderOpen({ ...loaderOpen, open: false });

    const handleSuccessAlert = (message: string) => {
        setAlert({
            open: true,
            message,
            severity: "success",
        });
    };

    const handleErrorAlert = (message: string) => {
        setAlert({
            open: true,
            message,
            severity: "error",
        });
    };

    const handleInfoAlert = (message: string) => {
        setAlert({
            open: true,
            message,
            severity: "info",
        });
    };

    const handleWarningAlert = (message: string) => {
        setAlert({
            open: true,
            message,
            severity: "warning",
        });
    };

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === "clickaway") {
            return;
        }

        setAlert({ ...alert, open: false });
    };

    return (
        <LayoutContext.Provider value={{ alert, handleCloseAlert, handleCloseLoader, handleErrorAlert, handleInfoAlert, handleOpenLoader, handleSuccessAlert, handleWarningAlert, loaderOpen }}>
            {children}
        </LayoutContext.Provider>
    );
};

export const useLayout = () => useContext(LayoutContext) as LayoutContextProps;

export interface AlertProps {
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
}

export interface LoaderProps {
    open: boolean;
    text: string;
}
